import React, { useState, useCallback, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import { WarehouseInfo, PrefInfo, UpdateWarehouseRequest } from "../types";
import { areaData } from "../constants";
import Surround from "./Surround";

interface PrefEditModalProps {
  open: boolean;
  onClose: () => void;
  warehouseInfo: WarehouseInfo | null;
  onUpdateWarehouse: (updatedWarehouse: UpdateWarehouseRequest) => void;
  registeredPrefectures: string[];
  filteredType: "近隣倉庫" | "センター倉庫";
}

/**
 *倉庫情報編集モーダル
 */
export default function PrefEditModal({
  open,
  onClose,
  warehouseInfo,
  onUpdateWarehouse,
  registeredPrefectures,
  filteredType,
}: PrefEditModalProps) {
  const { showAlert } = useContext(SuccessAlertContext);
  const [depotName, setDepotName] = useState("");
  const [prefInfos, setPrefInfos] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (warehouseInfo) {
      // console.log("登録済みの都道府県:", registeredPrefectures);
      setDepotName(warehouseInfo.depot_name);
      setPrefInfos(
        warehouseInfo.pref_infos?.reduce(
          (acc, pref) => {
            acc[pref.pref_name] = true;
            return acc;
          },
          {} as { [key: string]: boolean },
        ) || {},
      );
    }
  }, [warehouseInfo]);

  const resetState = () => {
    setDepotName("");
    setPrefInfos({});
  };

  const handleSave = () => {
    if (!warehouseInfo) return;

    // 倉庫名が入力されていない場合
    if (!depotName.trim()) {
      showAlert("倉庫名を入力して下さい。", "error");
      return;
    }

    // 選択された都道府県のチェック
    const selectedPrefectures = Object.values(prefInfos).filter((isSelected) => isSelected);
    if (selectedPrefectures.length === 0) {
      showAlert("都道府県を最低1つは選択してください。", "error");
      return;
    }

    const updatedPrefInfos: PrefInfo[] = Object.entries(prefInfos)
      .filter(([_, selected]) => selected)
      .flatMap(([prefName]) =>
        areaData.flatMap((area) =>
          area.prefectures
            .filter((p) => p.pref_name === prefName)
            .map((pref) => ({
              pref_cd: pref.pref_cd,
              pref_name: pref.pref_name,
            })),
        ),
      );

    const updatedWarehouse: UpdateWarehouseRequest = {
      ...warehouseInfo,
      operation: 1,
      depot_name: depotName,
      pref_infos: updatedPrefInfos,
      depot_kind: filteredType === "近隣倉庫" ? 1 : 0,
    };
    onUpdateWarehouse(updatedWarehouse);
    onClose();
    resetState();
  };

  const toggleAreaSelection = useCallback(
    (area: string, checked: boolean) => {
      setPrefInfos((prev) => {
        const newSelections = { ...prev };
        areaData
          .find((data) => data.area === area)
          ?.prefectures.forEach((pref) => {
            const isPrefRegistered =
              registeredPrefectures.includes(pref.pref_cd) ||
              warehouseInfo?.pref_infos.some((wPref) => wPref.pref_name === pref.pref_name);
            if (!isPrefRegistered) {
              newSelections[pref.pref_name] = checked;
            }
          });
        return newSelections;
      });
    },
    [registeredPrefectures, warehouseInfo],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box p={2}>
        <DialogTitle>都道府県編集</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Box m={1} width="100%" display="flex" justifyContent="center" alignItems="center">
            <Typography variant="body1" sx={{ minWidth: "100px" }}>
              倉庫名：
            </Typography>
            <TextField
              label="倉庫名"
              size="medium"
              value={depotName}
              onChange={(e) => setDepotName(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </Box>

          {/* チェックボックス */}
          <Box mt={2} width="100%">
            <Surround title="配送範囲">
              <Box p={1.5}>
                {areaData.map((area) => (
                  <Box mt={2} key={area.area}>
                    <Surround title={area.area}>
                      <Box mt={2}>
                        <Box
                          position="absolute"
                          top={-15}
                          left={8}
                          px={1}
                          sx={{ backgroundColor: "white" }}
                          color="#8B4513"
                        >
                          {(() => {
                            // エリアの全都道府県が登録済みであるか確認
                            const allRegistered = area.prefectures.every((pref) =>
                              registeredPrefectures.includes(pref.pref_cd),
                            );

                            // エリアのすべての都道府県が選択されているか確認
                            const allSelected = area.prefectures.every(
                              (pref) =>
                                prefInfos[pref.pref_name] ||
                                registeredPrefectures.includes(pref.pref_cd),
                            );

                            // ラベルの設定
                            let label;
                            if (allRegistered) {
                              label = `${area.area}選択済`;
                            } else if (allSelected) {
                              label = `${area.area}全解除`;
                            } else {
                              label = `${area.area}全選択`;
                            }

                            return (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color="secondary"
                                    checked={allSelected}
                                    onChange={(e) => toggleAreaSelection(area.area, !allSelected)}
                                    disabled={allRegistered}
                                    sx={{ opacity: allRegistered ? 0.5 : 1 }}
                                  />
                                }
                                label={<span>{label}</span>}
                              />
                            );
                          })()}
                        </Box>
                        <Box px={2} py={1}>
                          {area.prefectures.map((prefecture) => {
                            const isRegistered = registeredPrefectures.includes(prefecture.pref_cd);
                            return (
                              <FormControlLabel
                                key={prefecture.pref_cd}
                                control={
                                  <Checkbox
                                    color="secondary"
                                    sx={{ opacity: isRegistered ? 0.5 : 1 }}
                                    checked={isRegistered || !!prefInfos[prefecture.pref_name]}
                                    onChange={(e) =>
                                      setPrefInfos((prev) => ({
                                        ...prev,
                                        [prefecture.pref_name]: e.target.checked,
                                      }))
                                    }
                                    disabled={isRegistered}
                                  />
                                }
                                label={prefecture.pref_name}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Surround>
                  </Box>
                ))}
              </Box>
            </Surround>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="custom-outlined" onClick={onClose}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleSave}>登録</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
